import React, { useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { GripVertical, X } from 'lucide-react';
import { AVAILABLE_COLUMNS, DEFAULT_COLUMNS, COLUMN_CATEGORIES } from '../config/sessionColumns';
import { usePreferences } from '../../../shared/context/preferencesContext';

const SessionColumnConfigModal = ({ isOpen, onClose, teamId }) => {
  const { updateSessionsTableColumns, resetSessionsTableColumns, getSessionsTableColumns, loading } = usePreferences();
  const [enabledColumns, setEnabledColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const current = getSessionsTableColumns();
      setEnabledColumns([...current.enabled]);
      setColumnOrder([...current.order]);
      setHasChanges(false);
    }
  }, [isOpen, getSessionsTableColumns]);

  const handleToggleColumn = (columnKey) => {
    const column = AVAILABLE_COLUMNS[columnKey];
    if (!column) return;
    if (column.required) return;

    const newEnabled = enabledColumns.includes(columnKey)
      ? enabledColumns.filter(key => key !== columnKey)
      : [...enabledColumns, columnKey];

    const requiredColumns = ['session_id', 'time'];
    const hasRequired = requiredColumns.every(col => newEnabled.includes(col));
    if (newEnabled.length < 2 || !hasRequired) return;

    setEnabledColumns(newEnabled);
    const newOrder = columnOrder.filter(key => newEnabled.includes(key));
    if (!enabledColumns.includes(columnKey)) {
      newOrder.push(columnKey);
    }
    setColumnOrder(newOrder);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await updateSessionsTableColumns(teamId, enabledColumns, columnOrder);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving session column preferences:', error);
      alert('Failed to save column preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!teamId) return;
    setSaving(true);
    try {
      await resetSessionsTableColumns(teamId);
      const current = getSessionsTableColumns();
      setEnabledColumns([...current.enabled]);
      setColumnOrder([...current.order]);
      setHasChanges(false);
    } catch (error) {
      console.error('Error resetting column preferences:', error);
      alert('Failed to reset column preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    const current = getSessionsTableColumns();
    setEnabledColumns([...current.enabled]);
    setColumnOrder([...current.order]);
    setHasChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  const orderedEnabledColumns = columnOrder.filter(key => enabledColumns.includes(key));
  const columnsByCategory = {};
  Object.keys(AVAILABLE_COLUMNS).forEach(key => {
    const column = AVAILABLE_COLUMNS[key];
    const category = column.category || 'other';
    if (!columnsByCategory[category]) columnsByCategory[category] = [];
    columnsByCategory[category].push({ key, ...column });
  });

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity" onClick={handleCancel} />
        <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-[#DDE2EE] rounded-xl">
          <div className="flex-1 overflow-y-auto bg-gray-50/30 p-6 relative">
            <button
              onClick={handleCancel}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 text-[#475569] hover:text-[#475569] transition-all duration-200 rounded-lg z-10"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-[#0f172a] mb-3" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Available Columns
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {Object.keys(COLUMN_CATEGORIES).map(category => {
                    const categoryColumns = columnsByCategory[category] || [];
                    if (categoryColumns.length === 0) return null;
                    return (
                      <div key={category} className="mb-4">
                        <div className="text-xs font-medium text-[#475569] mb-2 uppercase tracking-wider">
                          {COLUMN_CATEGORIES[category]}
                        </div>
                        {categoryColumns.map(({ key, label, required }) => {
                          const isEnabled = enabledColumns.includes(key);
                          const isDisabled = required || (isEnabled && enabledColumns.length <= 2);
                          return (
                            <label
                              key={key}
                              className={`flex items-center px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                isEnabled ? 'border' : 'bg-white border border-gray-200 hover:bg-gray-50'
                              } ${isDisabled && !required ? 'opacity-50 cursor-not-allowed' : ''}`}
                              style={isEnabled ? { backgroundColor: 'rgba(75, 87, 170, 0.2)', borderColor: '#4b57aa', color: '#0f172a' } : {}}
                            >
                              <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={() => handleToggleColumn(key)}
                                disabled={isDisabled}
                                className="mr-3 rounded border-gray-300 focus:ring-blue-500"
                              />
                              <span className="text-sm" style={{ fontFamily: 'Raleway, sans-serif', color: isEnabled ? '#0f172a' : '#475569' }}>
                                {label}
                                {required && <span className="ml-2 text-xs" style={{ opacity: isEnabled ? 0.8 : 0.7, color: isEnabled ? '#0f172a' : '#475569' }}>(required)</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#0f172a] mb-3" style={{ fontFamily: 'Raleway, sans-serif' }}>
                  Selected Columns (drag to reorder)
                </h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {orderedEnabledColumns.length === 0 ? (
                    <div className="text-sm text-[#475569] text-center py-8" style={{ fontFamily: 'Raleway, sans-serif' }}>No columns selected</div>
                  ) : (
                    orderedEnabledColumns.map((columnKey, index) => (
                      <DraggableColumnItem
                        key={columnKey}
                        columnKey={columnKey}
                        index={index}
                        label={AVAILABLE_COLUMNS[columnKey]?.label || columnKey}
                        isRequired={AVAILABLE_COLUMNS[columnKey]?.required || false}
                        onMove={(fromIndex, toIndex) => {
                          const newOrder = [...columnOrder];
                          const [removed] = newOrder.splice(fromIndex, 1);
                          newOrder.splice(toIndex, 0, removed);
                          setColumnOrder(newOrder);
                          setHasChanges(true);
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-t border-[#DDE2EE] bg-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={handleReset}
                disabled={saving || loading}
                className="px-4 py-2 text-sm font-medium text-[#475569] bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontFamily: 'Raleway, sans-serif' }}
              >
                Reset to Default
              </button>
              {hasChanges && <span className="text-xs text-amber-600" style={{ fontFamily: 'Raleway, sans-serif' }}>You have unsaved changes</span>}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving || loading || !hasChanges}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                style={{ fontFamily: 'Raleway, sans-serif', backgroundColor: (!saving && !loading && hasChanges) ? '#4b57aa' : '#9ca3af', cursor: (!saving && !loading && hasChanges) ? 'pointer' : 'not-allowed' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

const DraggableColumnItem = ({ columnKey, index, label, isRequired, onMove }) => {
  const [{ isDragging }, drag] = useDrag({ type: 'column', item: () => ({ columnKey, index }), collect: (monitor) => ({ isDragging: monitor.isDragging() }) });
  const [{ isOver }, drop] = useDrop({
    accept: 'column',
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        onMove(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
    collect: (monitor) => ({ isOver: monitor.isOver() })
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      className={`flex items-center px-3 py-2 rounded-lg border cursor-move transition-all ${isDragging ? 'opacity-50' : isOver ? 'border-[#4b57aa]' : 'hover:bg-gray-50'}`}
      style={{ backgroundColor: isOver ? '#4b57aa' : '#ffffff', borderColor: isOver ? '#4b57aa' : '#e5e7eb', color: isOver ? '#ffffff' : '#0f172a' }}
    >
      <GripVertical className="h-4 w-4 mr-2" style={{ color: isOver ? '#ffffff' : '#9ca3af' }} />
      <span className="flex-1 text-sm" style={{ fontFamily: 'Raleway, sans-serif', color: isOver ? '#ffffff' : '#0f172a' }}>
        {label}
        {isRequired && <span className="ml-2 text-xs" style={{ opacity: isOver ? 0.9 : 0.7, color: isOver ? '#ffffff' : '#475569' }}>(required)</span>}
      </span>
    </div>
  );
};

export default SessionColumnConfigModal;
